import {
  CONFIDENCE_TIER_DETAILS,
  CONFIDENCE_TIER_LABELS,
  ConfidenceEstimate,
  type ResponseConfidence,
  Tooltip,
} from '@civilgrid/ui/components';
import { COMPLETED_REQUEST_TOOLTIP, type EstTimeState, NO_ESTIMATE_TOOLTIP } from './estimatedResponseTime';

/** Hover copy for a placed request: what the range counts from, and how much history backs it. */
export function getRequestedTooltip(estimate: string, confidence: ResponseConfidence | null): string {
  const parts = [`${estimate} from time of request.`];
  if (confidence)
    parts.push(`Confidence: ${CONFIDENCE_TIER_LABELS[confidence]} — ${CONFIDENCE_TIER_DETAILS[confidence]}`);
  return parts.join(' ');
}

/** Plain cell text, with the hover that explains it when there is one. */
const HoverText = ({ testId, tooltip, children }: { testId: string; tooltip?: string; children: string }) => {
  if (!tooltip) return <div data-testid={testId}>{children}</div>;
  return (
    // Radix puts focus and aria-describedby on the trigger, so the test id belongs there too.
    <Tooltip
      text={tooltip}
      delayDuration={200}
      className="max-w-60"
      triggerClassName="cursor-help"
      data-testid={testId}
    >
      {children}
    </Tooltip>
  );
};

/** Renders one Est time cell from its state; shared by the utilities table and the Select Assets step. */
export default function EstTimeCell({ state, testId }: { state: EstTimeState; testId: string }) {
  switch (state.kind) {
    case 'suppressed':
      return <HoverText testId={testId}>—</HoverText>;
    case 'noEstimate':
      return (
        <HoverText testId={testId} tooltip={NO_ESTIMATE_TOOLTIP}>
          —
        </HoverText>
      );
    case 'delivered':
      return (
        <HoverText testId={testId} tooltip={COMPLETED_REQUEST_TOOLTIP}>
          Delivered
        </HoverText>
      );
    case 'completed':
      return (
        <HoverText testId={testId} tooltip={COMPLETED_REQUEST_TOOLTIP}>
          Completed
        </HoverText>
      );
    case 'estimate':
      return <ConfidenceEstimate label={state.label} confidence={state.confidence} showTooltip data-testid={testId} />;
    case 'requested':
      // The range is hover-only here, so a row with no usable stats shows the date and nothing else.
      return (
        <HoverText
          testId={testId}
          tooltip={state.estimate ? getRequestedTooltip(state.estimate, state.confidence) : undefined}
        >
          {state.requestedOnLabel}
        </HoverText>
      );
  }
}
